import React, { memo, useRef, useCallback, useEffect } from 'react';

/**
 * DragHandle Component
 * 
 * Renders a zoom-independent drag handle for nodes.
 * Positioned at the top-left of the node.
 * 
 * IMPORTANT: This component does NOT render its own menu.
 * Instead, it dispatches a custom event ('openTaskDragMenu') when clicked,
 * which TaskNode listens for and opens its DragHandleMenu.
 * This ensures there's only ONE menu component to maintain.
 * 
 * Click to show menu (via event dispatch), drag to move node.
 */
const DragHandle = memo(({
  task,
  screenPos,
  onMouseDown,
  menuOnly = false,
}) => {
  const handleRef = useRef(null);
  const clickStartRef = useRef(null);
  const isTrackingRef = useRef(false);

  // Handle document mouseup to detect click vs drag
  const handleDocumentMouseUp = useCallback((e) => {
    if (!clickStartRef.current || !isTrackingRef.current) return;
    
    // Only respond to left-button releases
    if (e.button !== 0) {
      clickStartRef.current = null;
      isTrackingRef.current = false;
      return;
    }
    
    const deltaX = Math.abs(e.clientX - clickStartRef.current.x);
    const deltaY = Math.abs(e.clientY - clickStartRef.current.y);
    const deltaTime = Date.now() - clickStartRef.current.time;
    
    // If it was a quick click without much movement, dispatch event to open menu
    if (deltaX < 5 && deltaY < 5 && deltaTime < 300) {
      if (handleRef.current) {
        const rect = handleRef.current.getBoundingClientRect();
        
        // Dispatch custom event for TaskNode to open its DragHandleMenu
        window.dispatchEvent(new CustomEvent('openTaskDragMenu', {
          detail: { 
            taskId: task.id, 
            // Pass the handle's bounding rect so the menu can position itself
            handleRect: {
              top: rect.top,
              left: rect.left,
              right: rect.right,
              bottom: rect.bottom,
            }
          }
        }));
      }
    }
    
    clickStartRef.current = null;
    isTrackingRef.current = false;
  }, [task.id]);

  // Set up and tear down the document mouseup listener
  useEffect(() => {
    document.addEventListener('mouseup', handleDocumentMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, [handleDocumentMouseUp]);

  const openMenu = (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const rect = handleRef.current?.getBoundingClientRect();
    if (!rect) return;
    window.dispatchEvent(new CustomEvent('openTaskDragMenu', {
      detail: {
        taskId: task.id,
        handleRect: {
          top: rect.top,
          left: rect.left,
          right: rect.right,
          bottom: rect.bottom,
        },
      },
    }));
  };

  const handleMouseDownInternal = (e) => {
    if (menuOnly) {
      openMenu(e);
      return;
    }
    // Only track left clicks (button 0) - right clicks are handled by onContextMenu
    if (e.button !== 0) return;
    
    // Record click start position to detect drag vs click
    clickStartRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    isTrackingRef.current = true;
    // Call the parent's onMouseDown for drag functionality
    onMouseDown?.(e);
  };

  return (
    <div
      ref={handleRef}
      className={`handles-layer__drag-handle${menuOnly ? ' handles-layer__drag-handle--menu' : ''}`}
      data-task-id={task.id}
      style={{
        left: `${screenPos.x}px`,
        top: `${screenPos.y}px`,
      }}
      onMouseDown={handleMouseDownInternal}
      title={menuOnly ? 'Options' : 'Click for options, drag to move'}
      role={menuOnly ? 'button' : undefined}
      aria-label={menuOnly ? 'Mission options' : undefined}
    >
      {menuOnly ? <span className="handles-layer__menu-mark" aria-hidden>⋮</span> : null}
      {menuOnly ? null : <div className="handles-layer__drag-dots">
        <span className="handles-layer__drag-dot" />
        <span className="handles-layer__drag-dot" />
        <span className="handles-layer__drag-dot" />
        <span className="handles-layer__drag-dot" />
        <span className="handles-layer__drag-dot" />
        <span className="handles-layer__drag-dot" />
      </div>}
    </div>
  );
});

DragHandle.displayName = 'DragHandle';

export default DragHandle;
